import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo
} from 'react';
import {
  BottomInputArea,
  type BottomInputAreaProps
} from '../../components/BottomInputArea';
import {
  type TopHeaderProps,
  type HeaderCapabilityOption
} from '../../components/TopHeader';
import {
  RightSidebar,
  type HistoryGroup,
  type ContextResource
} from './RightSidebar';
import { useDifyCapability } from '../../hooks/useDifyCapability';
import { useStreamingMessage } from '../../hooks/useStreamingMessage';
import type { PatternFeatureKey } from '../../types/aiDifyCapability';
import { AiDifyCapability } from '../../types/aiDifyCapability';
import { NotificationBadge } from '../../components/NotificationBadge';
import type { TitleUpdateEvent } from '../../services/notificationSSEService';
import type { CodeSelection } from '../../../AIPlcCoderPlugin/types';
import { CodeSelectionTag } from '../../../AIPlcCoderPlugin/components/CodeSelectionTag';
import { ChatWelcome } from './ChatWelcome';
import { useSkillAssets } from '../../hooks/useSkillAssets';
import { useCodePreview } from '../../hooks/useCodePreview';
import { CodePreviewPanel } from '../../components/CodePreviewPanel';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useStreamingSync } from '../../hooks/useStreamingSync';
import { useConversationManager } from '../../hooks/useConversationManager';
import { conversationHistoryService } from '../../services/conversationHistoryService';
import { useHistoryGroups } from '../../hooks/useHistoryGroups';
import { MessageList } from './MessageList';
import { extractQuickReplies } from '../../utils/extractQuickReplies';
import { extractInteractiveTable } from '../../utils/extractInteractiveTable';
import { ChatTimeline } from './ChatTimeline';
import type { AiChatPageProps, AiChatPageRef } from './types';
import '../../styles/featureBar.css';
import '../../styles/aiLoader.css';
import '../../styles/chatTimeline.css';

export type { AiChatPageProps, AiChatPageRef } from './types';

export const AiChatPage = forwardRef<AiChatPageRef, AiChatPageProps>(({
  modelLabel,
  onClickUpgrade,
  value,
  onChangeInput,
  onClickMic,
  onClickSend,
  businessType = 'default',
  businessId = null,
  defaultConversationId,
  defaultCapabilityCode,
  capabilityDisabled = false,
  contextResources,
  historyGroups,
  onClickNewChat,
  onSelectConversation,
  onSelectResource,
  onClickShare,
  onMessageReceived,
  sidebarMode = 'fixed',
  showContextResources = false,
  codeSelection = null,
  onClearCodeSelection,
  onBeforeSend,
  showDesignButton = false,
  onDesign,
  onInsertCode,
  showInsertCodeBtn = false,
  extraInputs,
}, ref) => {

  // ─── 能力管理 ─────────────────────

  const { capabilities, loading: difyLoading, error: difyError } = useDifyCapability();
  const [currentCapability, setCurrentCapability] = useState<AiDifyCapability | null>(null);
  const [currentCapabilityId, setCurrentCapabilityId] = useState<number | undefined>(undefined);
  const [selectedPattern, setSelectedPattern] = useState<PatternFeatureKey | null>(null);

  // ─── 核心 hooks ─────────────────────

  const streaming = useStreamingMessage();
  const { chatAreaRef, endOfListRef, scrollToBottom } = useScrollManager();

  // ★ 共享的 streamingMsgIdRef（解决循环依赖）
  const streamingMsgIdRef = useRef<string | null>(null);

  const skillsEnabled = !!currentCapability?.features?.skills;
  const { options: skillOptions, loading: skillsLoading, selectedIds: selectedSkillIds, toggleSkill } =
    useSkillAssets(currentCapabilityId, skillsEnabled);

  // ─── 会话管理（传入共享 ref） ─────────────────────

  const convManager = useConversationManager({
    businessType,
    businessId,
    currentCapability,
    currentCapabilityId,
    skillsEnabled,
    selectedSkillIds,
    selectedPattern,
    streaming,
    streamingMsgIdRef,
    scrollToBottom,
    onMessageReceived,
    onBeforeSend,
    extraInputs, // ← 新增：传递额外输入参数
    previewLastHtmlMessage: (msgs) => {
      // 延迟调用，因为 codePreview 还没初始化
      // 实际上 previewLastHtmlMessage 在 loadConversation 时才会被调用
      // 此时 codePreview 已经可用了
    }
  });

  // ─── 代码预览 ─────────────────────

  const {
    previewOpen: codePreviewOpen,
    previewUrl: codePreviewUrl,
    loading: codePreviewLoading,
    previewMessage: previewCodeMessage,
    closePreview: closeCodePreview,
    refreshPreview: refreshCodePreview,
    previewLastHtmlMessage
  } = useCodePreview(convManager.messages);

  // ─── 流式同步（传入共享 ref） ─────────────────────

  useStreamingSync({
    streamingState: streaming.state,
    setMessages: convManager.setMessages,
    scrollToBottom,
    streamingMsgIdRef,
    onCompleted: (state) => {
      if (state.content) {
        onMessageReceived?.(state.content, {
          workflowTrace: state.workflowTrace ?? null,
          rawStreamingState: state as unknown as Record<string, unknown>,
        });
      }
    },
    onError: () => {
      void convManager.notificationRefreshRef.current?.();
    }
  });

  // ─── 能力初始化 ─────────────────────

  useEffect(() => {
    if (!difyLoading && !difyError && capabilities.length > 0) {
      if (currentCapabilityId != null) return;
      const enabledList = capabilities.filter(item => item.enabled);
      let initial: AiDifyCapability | undefined;
      console.log("AIIIII defaultCapabilityCode:", defaultCapabilityCode);
      
      if (defaultCapabilityCode) {
        initial = enabledList.find(item => item.code === defaultCapabilityCode)
          ?? capabilities.find(item => item.code === defaultCapabilityCode);
      }
      if (!initial) {
        initial = enabledList.length > 0 ? enabledList[0] : capabilities[0];
      }
      if (initial) {
        setCurrentCapability(initial);
        setCurrentCapabilityId(initial.id);
      }
    }
  }, [capabilities, currentCapabilityId, difyLoading, difyError, defaultCapabilityCode]);

  // ─── defaultConversationId 跳转 ─────────────────────

  useEffect(() => {
    if (defaultConversationId && typeof defaultConversationId === 'number') {
      convManager.setViewMode('chat');
      // 通过 selectConversation 加载消息，而不是只设置 ID
      void convManager.selectConversation(
        String(defaultConversationId),
        `对话 #${defaultConversationId}`
      );
    }
  }, [defaultConversationId]);

  // ─── 自动恢复上次激活对话 ─────────────────────
  // 必须在 capabilities 加载完成后执行，这样才能正确切换 capability，避免 Dify 404

  const hasAutoRestoredRef = useRef(false);

  useEffect(() => {
    // 有外部指定对话 或 能力尚未加载完毕 时跳过
    if (defaultConversationId || difyLoading || capabilities.length === 0) return;
    if (hasAutoRestoredRef.current) return;
    hasAutoRestoredRef.current = true;

    const run = async (): Promise<void> => {
      try {
        const active = await conversationHistoryService.getActiveConversation(businessType);
        if (!active) return;
        // 先切换到该对话对应的能力（与 handleSelectConversation 逻辑保持一致）
        if (active.dify_capability_id != null) {
          const targetCap = capabilities.find(cap => cap.id === active.dify_capability_id);
          if (targetCap) {
            setCurrentCapability(targetCap);
            setCurrentCapabilityId(targetCap.id);
          }
        }
        // 再加载对话消息
        await convManager.selectConversation(String(active.id), active.title ?? `对话 #${active.id}`);
        scrollToBottom(true);
      } catch (error) {
        console.error('[AiChatPage] 自动恢复上次对话失败:', error);
      }
    };
    void run();
  // capabilities 加载完毕后触发，hasAutoRestoredRef 防止重复执行
  }, [difyLoading, capabilities.length]);

  // ─── 历史分组 + "生成中"状态管理 ─────────────────────

  const [runningConversationDbIds, setRunningConversationDbIds] = useState<ReadonlySet<number>>(new Set());

  // streaming 开始 → 加入 running set；留在原对话且结束 → 移除
  useEffect(() => {
    const id = convManager.activeConversationDbId;
    if (streaming.state.status === 'streaming' && id != null) {
      setRunningConversationDbIds(prev => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    } else if (streaming.state.status !== 'streaming' && id != null) {
      // 用户留在原对话上，流式结束 → 从 set 中移除
      setRunningConversationDbIds(prev => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [streaming.state.status, convManager.activeConversationDbId]);

  // SSE 通知到达 → unreadConversationIds 更新 → 自动从 running set 中移除已完成的
  useEffect(() => {
    setRunningConversationDbIds(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        if (convManager.unreadConversationIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [convManager.unreadConversationIds]);

  const dynamicHistoryGroups = useHistoryGroups({
    dbConversations: convManager.dbConversations,
    capabilities,
    activeConversationDbId: convManager.activeConversationDbId,
    unreadConversationIds: convManager.unreadConversationIds,
    runningConversationDbIds,
    historyGroupsOverride: historyGroups,
    lastMessageTimes: convManager.lastMessageTimes
  });

  // ─── 事件处理器 ─────────────────────

  /** AI 是否正在回复中（禁止重复发送） */
  const isSending = streaming.state.status === 'streaming';

  const handleChangeInput = useCallback((val: string): void => {
    convManager.setDraftText(val);
    onChangeInput?.(val);
  }, [onChangeInput, convManager]);

  const handleSelectCapability = useCallback((id: number): void => {
    if (capabilityDisabled) return;
    const found = capabilities.find(item => item.id === id);
    if (!found) return;
    setCurrentCapability(found);
    setCurrentCapabilityId(found.id);
    setSelectedPattern(null);
    convManager.resetConversation();
  }, [capabilities, convManager]);

  /** 新建对话：如果有默认能力则重置回去 */
  const handleNewChat = useCallback((): void => {
    if (defaultCapabilityCode) {
      const defaultCap = capabilities.find(item => item.code === defaultCapabilityCode);
      if (defaultCap && defaultCap.id !== currentCapabilityId) {
        setCurrentCapability(defaultCap);
        setCurrentCapabilityId(defaultCap.id);
        setSelectedPattern(null);
      }
    }
    convManager.resetConversation();
    onClickNewChat?.();
  }, [defaultCapabilityCode, capabilities, currentCapabilityId, convManager, onClickNewChat]);

  const handleSend = useCallback((): void => {
    if (isSending) return;
    onClickSend?.();
    const current = convManager.draftText ?? '';
    if (!current.trim()) return;
    if (convManager.viewMode === 'home') convManager.setViewMode('chat');
    if (codePreviewOpen) closeCodePreview();
    void convManager.sendMessage(current);
  }, [isSending, convManager, onClickSend, codePreviewOpen, closeCodePreview]);

  const handlePromptSelect = useCallback((text: string) => {
    if (convManager.viewMode === 'home') convManager.setViewMode('chat');
    void convManager.sendMessage(text);
  }, [convManager]);

  /** ★ 快捷回复：直接发送指定文本 */
  const handleQuickReply = useCallback((text: string) => {
    if (isSending) return;
    // 安全保护：确保 text 是字符串（防御 React 16 事件系统异常）
    const replyText = typeof text === 'string' ? text : String(text);
    if (!replyText.trim()) return;
    if (convManager.viewMode === 'home') convManager.setViewMode('chat');
    void convManager.sendMessage(replyText);
  }, [isSending, convManager]);

  /** ★ 表格交互提交：发送组装好的确认消息 */
  const handleTableSubmit = useCallback((message: string) => {
    if (isSending) return;
    if (!message.trim()) return;
    if (convManager.viewMode === 'home') convManager.setViewMode('chat');
    void convManager.sendMessage(message);
  }, [isSending, convManager]);

  const handleSelectConversation = useCallback(async (id: string, title: string): Promise<void> => {
    onSelectConversation?.(id);
    const convIdNum = Number(id);

    // 切回 running 对话 → 从 set 中移除（用户正在看它了）
    setRunningConversationDbIds(prev => {
      if (!prev.has(convIdNum)) return prev;
      const next = new Set(prev);
      next.delete(convIdNum);
      return next;
    });

    const targetConv = convManager.dbConversations.find(c => c.id === convIdNum);
    if (targetConv?.dify_capability_id != null) {
      const targetCap = capabilities.find(cap => cap.id === targetConv.dify_capability_id);
      if (targetCap) {
        setCurrentCapability(targetCap);
        setCurrentCapabilityId(targetCap.id);
      }
    }
    await convManager.selectConversation(id, title);
    // 选择对话后，强制滚动到底部，无动画效果
    scrollToBottom(true);
  }, [onSelectConversation, convManager, capabilities, scrollToBottom]);

  const handleTitleUpdate = useCallback((event: TitleUpdateEvent) => {
    if (convManager.activeConversationDbId === event.conversationId) {
      convManager.setActiveConversationTitle(event.title);
    }
    convManager.setDbConversations(prev =>
      prev.map(conv =>
        conv.id === event.conversationId ? { ...conv, title: event.title } : conv
      )
    );
  }, [convManager]);

  const handleNotificationClick = useCallback((id: number) => {
    void handleSelectConversation(String(id), `对话 #${id}`);
  }, [handleSelectConversation]);

  // ─── ★ 恢复数据回调 ─────────────────────

  const handleRestore = useCallback((version: string): void => {
    const restoreText = `请恢复表格的数据到${version}版本`;
    if (convManager.viewMode === 'home') convManager.setViewMode('chat');
    void convManager.sendMessage(restoreText, { version });
  }, [convManager]);

  // ─── ref 暴露 ─────────────────────

  useImperativeHandle(ref, () => ({
    resetConversation: convManager.resetConversation,
    /** ★ 新增：程序化发送消息 */
    sendMessage: async (text: string, overrideInputs?: Readonly<Record<string, unknown>>): Promise<void> => {
      // 自动切换到聊天视图
      convManager.setViewMode('chat');
      await convManager.sendMessage(text, overrideInputs);
    },
  }), [convManager.resetConversation, convManager.sendMessage, convManager.setViewMode]);

  // ─── 通知刷新注入 ─────────────────────

  useEffect(() => {
    convManager.notificationRefreshRef.current = convManager.loadUnreadConversationIds;
  }, [convManager]);

  // ─── 计算值 ─────────────────────

  const capabilitiesOptions: readonly HeaderCapabilityOption[] = useMemo(() =>
    capabilities.filter(cap => cap.enabled).map(cap => ({ id: cap.id, name: cap.name, type: cap.type })),
    [capabilities]
  );

  /** 当前能力的欢迎页配置 */
  const welcomeConfig = useMemo(() => currentCapability?.welcome_config, [currentCapability]);

  /** ★ 从最后一条 AI 消息中提取快捷回复建议 */
  const quickReplies = useMemo(() => {
    const msgs = convManager.messages;
    if (msgs.length === 0) return [];
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg.role !== 'assistant' || lastMsg.status !== 'done') return [];
    return extractQuickReplies(lastMsg.content);
  }, [convManager.messages]);

  /** ★ 从最后一条 AI 消息中提取可交互表格 */
  const tableInteraction = useMemo(() => {
    const msgs = convManager.messages;
    if (msgs.length === 0) return null;
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg.role !== 'assistant' || lastMsg.status !== 'done') return null;
    return extractInteractiveTable(lastMsg.content);
  }, [convManager.messages]);

  /** 表格表单 key，用于在新消息到达时重置表单内部状态 */
  const tableInteractionKey = useMemo(() => {
    const msgs = convManager.messages;
    if (msgs.length === 0) return '';
    return msgs[msgs.length - 1].id;
  }, [convManager.messages]);

  /** ★ 能力数据是否还没准备好（正在加载 或 加载完但还没选中能力） */
  const capabilityLoading = useMemo(
    () => difyLoading || (capabilities.length > 0 && currentCapability == null),
    [difyLoading, capabilities.length, currentCapability]
  );

  const sidebarProps = useMemo(() => ({
    assistantName: currentCapability?.name ?? '智能助手 v2.6',
    capabilities: capabilitiesOptions,
    currentCapabilityId,
    onSelectCapability: handleSelectCapability,
    capabilityDisabled: defaultCapabilityCode ? true : false,
    contextResources,
    historyGroups: dynamicHistoryGroups,
    onClickNewChat: handleNewChat,
    onClickShare: onClickShare ?? (() => {}),
    onSelectConversation: handleSelectConversation,
    onSelectResource: onSelectResource ?? (() => {}),
    sidebarMode: sidebarMode as 'fixed' | 'drawer',
    drawerOpen: convManager.drawerOpen,
    onDrawerClose: () => convManager.setDrawerOpen(false),
    showContextResources,
    onLoadMoreHistory: convManager.loadMoreConversations,
    historyHasMore: convManager.historyHasMore,
    historyLoadingMore: convManager.historyLoadingMore,
  }), [
    currentCapability, capabilitiesOptions, currentCapabilityId,
    handleSelectCapability, capabilityDisabled, contextResources,
    dynamicHistoryGroups, convManager, onClickShare,
    handleSelectConversation, onSelectResource, sidebarMode,
    showContextResources
  ]);

  // ─── Render ─────────────────────

  return (
    <div className={`chat-layout ${codePreviewOpen ? 'chat-layout--preview' : ''}`}>
      <div className="chat-main">

        <div className="chat-header-actions">
          <NotificationBadge
            onClickNotification={handleNotificationClick}
            pollingInterval={30000}
            onRefreshRef={convManager.notificationRefreshRef}
            onUnreadUpdate={convManager.loadUnreadConversationIds}
            onTitleUpdate={handleTitleUpdate}
            businessType={businessType}
          />
          {sidebarMode === 'drawer' && (
            <button type="button" onClick={() => convManager.setDrawerOpen(true)} title="打开历史记录">
              <i className="fas fa-bars" />
            </button>
          )}
        </div>

        {convManager.viewMode !== 'home' && (
          <div className="chat-title">{convManager.activeConversationTitle}</div>
        )}

        {convManager.viewMode === 'home' ? (
          <div className="chat-welcome-scroll-wrapper">
            <ChatWelcome 
              onSelectPrompt={handlePromptSelect} 
              showDesignButton={showDesignButton} 
              welcomeConfig={welcomeConfig} 
              capabilityLoading={capabilityLoading}
            />
          </div>
        ) : (
          <div className="chat-area-wrapper has-timeline">
            <div className="chat-area" ref={chatAreaRef}>
              {convManager.conversationLoading ? (
                <div className="chat-conv-loading">
                  <i className="fa-solid fa-circle-notch fa-spin" />
                  <span>加载记录中...</span>
                </div>
              ) : (
                <>
                  <MessageList 
                    messages={convManager.messages} 
                    onPreview={previewCodeMessage} 
                    showDesignButton={showDesignButton}
                    onDesign={onDesign}
                    onInsertCode={onInsertCode}
                    showInsertCodeBtn={showInsertCodeBtn}
                    onRestore={handleRestore}
                  />
                  <div ref={endOfListRef} style={{ height: 1 }} />
                </>
              )}
            </div>
            {!convManager.conversationLoading && (
              <ChatTimeline
                messages={convManager.messages}
                chatAreaRef={chatAreaRef}
              />
            )}
          </div>
        )}

        <BottomInputArea
          value={convManager.draftText}
          onChangeInput={handleChangeInput}
          onClickMic={onClickMic}
          onClickSend={handleSend}
          topAddon={codeSelection ? (
            <CodeSelectionTag selection={codeSelection} onClear={onClearCodeSelection ?? (() => {})} />
          ) : undefined}
          capabilityFeatures={currentCapability?.features}
          skillOptions={skillOptions}
          skillsLoading={skillsLoading}
          selectedSkillIds={selectedSkillIds}
          onToggleSkill={toggleSkill}
          selectedPattern={selectedPattern}
          onSelectPattern={setSelectedPattern}
          placeholder={currentCapability?.placeholder}
          disabled={isSending}
          quickReplies={quickReplies}
          onQuickReply={handleQuickReply}
          tableInteraction={tableInteraction}
          onTableSubmit={handleTableSubmit}
          tableInteractionKey={tableInteractionKey}
        />

        {sidebarMode === 'drawer' && <RightSidebar {...sidebarProps} />}
      </div>

      {codePreviewOpen && (
        <CodePreviewPanel
          previewUrl={codePreviewUrl}
          loading={codePreviewLoading}
          onClose={closeCodePreview}
          onRefresh={refreshCodePreview}
        />
      )}

      {sidebarMode === 'fixed' && !codePreviewOpen && <RightSidebar {...sidebarProps} />}
    </div>
  );
});